import {
  Component,
  computed,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  Signal,
  ViewChild,
} from '@angular/core';
import {
  Agent,
  AgentKeys,
  AGENT_STATUS,
  ApproveDenyReason,
  ApproveDenyReasonKeys,
  ApproveDenyReasonVisibilityLevel,
  ChangeSourceType,
  AgencyKeys,
  AGENT_TYPE,
  Entity,
  EntityPermissionActivityKeys,
  ActiveLookup,
  agencyTypeMap,
} from '@ag-common-lib/public-api';
import { DxFormComponent } from 'devextreme-angular';
import { Observable, combineLatest, distinctUntilChanged, firstValueFrom, map, of, shareReplay } from 'rxjs';
import { ModalWindowComponent } from '../modal-window/modal-window.component';
import { AgentDispositionModalService, ChangeAgentStatusConfig } from './agent-disposition-modal.service';
import { AgentService } from '../../services/agent.service/agent.service';
import { AgentApproveDenyReasonsService } from '../../services/agent-approve-deny-reason.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService, ChangeSourceTypeService } from 'ag-common-svc/public-api';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApproveDenyReasonEditorConfig } from '../agent-editor/components/approve-deny-reasons/approve-deny-reasons-grid/approve-deny-reasons-grid.model';
import { ValidationCallbackData } from 'devextreme/common';
import { validateDxGroups } from 'ag-common-svc/lib/utils/validation';
import { PortalDataService } from 'ag-common-svc/lib/services/portal.service';
import {
  AgencyHierarchyManagerMode,
  HierarchyChange,
} from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agency-hierarchy-management.model';
import { ModalWindowService } from '../modal-window/modal-window.service';
import { ModalWindowActionPayload, ModalWindowUserAction } from '../modal-window/modal-window.model';
import { AgentsTransferModalService } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agents-transfer-modal.service';
import { AgencyHierarchyManagementComponent } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agency-hierarchy-management.component';
import { AgencyHierarchyManagementService } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agency-hierarchy-management.service';
import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import { HierarchyValidationService } from 'ag-common-svc/lib/services/hierarchy/hierarchy-validation.service';

@Component({
  selector: 'ag-shr-agent-disposition-modal',
  templateUrl: './agent-disposition-modal.component.html',
  styleUrls: ['./agent-disposition-modal.component.scss'],
  providers: [AgentDispositionModalService, AgentsTransferModalService],
})
export class AgentDispositionModalComponent {
  @HostBinding('class') className = 'agent-disposition-modal';
  @ViewChild('agentDispositionModalRef', { static: true }) agentDispositionModalComponent: ModalWindowComponent;
  @ViewChild('agentDispositionFormRef', { static: false }) agentDispositionFormComponent: DxFormComponent;
  @ViewChild('updateAgentStatusModalRef', { static: true }) updateAgentStatusModalComponent: ModalWindowComponent;
  @ViewChild('updateAgentStatusFormRef', { static: false }) updateAgentStatusFormComponent: DxFormComponent;
  @ViewChild('hierarchyManagementRef', { static: false })
  hierarchyManagementComponent?: AgencyHierarchyManagementComponent;

  private agentSearchService = inject(AgentSearchService);
  private hierarchyValidationService = inject(HierarchyValidationService);

  @Input() agentTypeVisible = true;
  @Input() mgaVisible = true;
  @Input() uplineVisible = true;
  @Input() agencyDirectorVisible = true;
  @Input() agentIdVisible = true;
  @Input() formColCount: number = 3;
  @Input() canEditMGA = false;
  @Input() canEditHierarchy = true;
  @Input() isRMDVisible: boolean = false;
  @Input() canViewApproveDenyReason: boolean = true;
  @Input() canEditApproveDenyReason: boolean = true;
  @Input() canDeleteApproveDenyReason: boolean = true;
  @Input() canCreateApproveDenyReason: boolean = true;
  @Input() approveDenyReasonVisibilityLevel: ApproveDenyReasonVisibilityLevel;
  @Input() sourceType: ChangeSourceType | null = null;
  // eslint-disable-next-line
  @Output() onAgentUpdated = new EventEmitter();

  protected agentDispositionFormDataSig: Signal<Partial<Agent>> = this.agentDispositionModalService.formDataSig;
  protected inProgress$: Observable<boolean> = this.agentDispositionModalService.inProgress$;
  protected AGENT_STATUS = AGENT_STATUS;
  protected AgentKeys = AgentKeys;
  protected AgencyKeys = AgencyKeys;
  protected BaseModelKeys = BaseModelKeys;
  protected ApproveDenyReasonKeys = ApproveDenyReasonKeys;
  protected Entity = Entity;
  protected EntityPermissionActivityKeys = EntityPermissionActivityKeys;
  protected agentDispositionValidationGroup = 'agentDispositionValidationGroup';
  protected agentIdValidationGroup = 'agentIdValidationGroup';
  protected isUpdateStatusInProgress = false;
  protected changeAgentStatusConfig: ChangeAgentStatusConfig;
  protected loggedInAgentType$ = this.authService.loggedInAgent$.pipe(
    map((loggedInAgent: Agent) => {
      return loggedInAgent?.[AgentKeys.agent_type];
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );
  protected loggedInAgentTypeSig = toSignal(this.loggedInAgentType$);
  protected isMgaLeadSig = computed(() => {
    const agent = this.agentDispositionFormDataSig();
    const agentType = agent?.[AgentKeys.agent_type];

    switch (agentType) {
      case AGENT_TYPE.MGA_ADMIN:
      case AGENT_TYPE.MGA_PRINCIPAL:
        return true;

      default:
        return false;
    }
  });

  protected mgasSig = this.agentDispositionModalService.mgasSig;
  protected mgaAgenciesSig = this.agentDispositionModalService.mgaAgenciesSig;
  private changeSourceTypeService = inject(ChangeSourceTypeService);
  protected AgencyHierarchyManagerMode = AgencyHierarchyManagerMode;
  private _sourceType: ChangeSourceType = this.changeSourceTypeService.sourceType();

  protected approveDenyReasonEditorConfigSig: Signal<ApproveDenyReasonEditorConfig> = computed(() => {
    const loggedInAgentType = this.loggedInAgentTypeSig();

    let visibilityLevel = ApproveDenyReasonVisibilityLevel.AgencyLevel;

    switch (loggedInAgentType) {
      case AGENT_TYPE.MGA_ADMIN:
      case AGENT_TYPE.MGA_PRINCIPAL:
        visibilityLevel = ApproveDenyReasonVisibilityLevel.MGALevel;
        break;
    }

    if (this.approveDenyReasonVisibilityLevel) {
      visibilityLevel = this.approveDenyReasonVisibilityLevel;
    }

    return { isVisibilityTypeLocked: true, initialApproveDenyReason: { visibilityLevel } };
  });

  protected approveDenyReasonsAllowedVisibilityLevelsSig: Signal<ApproveDenyReasonVisibilityLevel[]> = computed(() => {
    const levels = [ApproveDenyReasonVisibilityLevel.All, ApproveDenyReasonVisibilityLevel.AgencyLevel];
    const loggedInAgentType = this.loggedInAgentTypeSig();

    switch (loggedInAgentType) {
      case AGENT_TYPE.MGA_ADMIN:
      case AGENT_TYPE.MGA_PRINCIPAL:
        levels.push(ApproveDenyReasonVisibilityLevel.MGALevel);
        break;
    }

    return levels;
  });

  protected agencyManagersDataSourceSig = computed(() => {
    const agent = this.agentDispositionFormDataSig();
    const mgaId = agent?.[AgentKeys.p_mga_id];

    if (!mgaId) {
      return [];
    }

    return this.agentDispositionModalService.getAgencyManagersDataSource(mgaId);
  });
  protected inTransferInProgress$: Observable<boolean> = combineLatest([
    this.agentDispositionModalService.isTransferInProgress$,
    this.agentsTransferModalService.inProgress$,
  ]).pipe(map(items => items?.some(Boolean)));
  protected promptSettingsSig = this.agentsTransferModalService.promptSettingsSig;
  protected agentTransferConfigSig = this.agentsTransferModalService.agentTransferConfigSig;
  protected readonly transferPromptModalId = this.agentsTransferModalService.transferPromptModalId;
  protected readonly transferModalId = this.agentsTransferModalService.transferModalId;
  protected readonly hasHierarchyChangesSig = this.agencyHierarchyManagementService.hasHierarchyChangesSig;

  agentTypesSig: Signal<Partial<ActiveLookup>[]> = computed(() => {
    const agent = this.agentDispositionFormDataSig();
    const mgaId = agent?.[AgentKeys.p_mga_id];
    const agencyId = agent?.[AgentKeys.p_agency_id];
    const isMGAMember = agencyId && agencyId === mgaId;

    return [
      {
        value: AGENT_TYPE.MGA_PRINCIPAL,
        description: agencyTypeMap.get(AGENT_TYPE.MGA_PRINCIPAL),
        visible: isMGAMember,
      },
      { value: AGENT_TYPE.MGA_ADMIN, description: agencyTypeMap.get(AGENT_TYPE.MGA_ADMIN), visible: isMGAMember },
      {
        value: AGENT_TYPE.AGENCY_PRINCIPAL,
        description: agencyTypeMap.get(AGENT_TYPE.AGENCY_PRINCIPAL),
        visible: !!agencyId && !isMGAMember,
      },
      {
        value: AGENT_TYPE.AGENCY_ADMIN,
        description: agencyTypeMap.get(AGENT_TYPE.AGENCY_ADMIN),
        visible: !!agencyId && !isMGAMember,
      },
      { value: AGENT_TYPE.GENERAL_AGENT, description: agencyTypeMap.get(AGENT_TYPE.GENERAL_AGENT) },
      { value: AGENT_TYPE.CARRIER_REP, description: agencyTypeMap.get(AGENT_TYPE.CARRIER_REP), visible: false },
    ];
  });

  protected isAgentIdGenerationInProgress$ = this.agentDispositionModalService.isAgentIdGenerationInProgress$;
  protected isManagerFieldVisibleSig = this.agentDispositionModalService.isManagerFieldVisibleSig;

  constructor(
    private authService: AuthService,
    private toster: ToastrService,
    private agentService: AgentService,
    private agentDispositionModalService: AgentDispositionModalService,
    private agentApproveDenyReasonsService: AgentApproveDenyReasonsService,
    private portalDataService: PortalDataService,
    private modalWindowService: ModalWindowService,
    private agentsTransferModalService: AgentsTransferModalService,
    private agencyHierarchyManagementService: AgencyHierarchyManagementService,
  ) {}

  showPopup = async (agent: Agent) => {
    this.agentDispositionModalService.setFormData(agent);

    this.agentDispositionModalComponent.showModal();
  };

  protected handleSave = async () => {
    try {
      const isValid = await validateDxGroups([this.agentDispositionValidationGroup, this.agentIdValidationGroup]);

      if (!isValid) {
        return;
      }

      await this.agentDispositionModalService.handleSave(this.sourceType);

      this.onAgentUpdated.emit();
      this.agentDispositionModalComponent.forceCloseModal();
    } catch (error) {}
  };

  protected handleFormPopupClose = this.agentDispositionModalService.onCancelEdit;

  protected handleChangeAgentStatus = async e => {
    const validationResults = this.updateAgentStatusFormComponent.instance.validate();

    if (validationResults.isValid) {
      const formData = this.updateAgentStatusFormComponent.formData;
      const userEmail = await firstValueFrom(this.authService.currentUser$.pipe(map(user => user?.email)));
      const approveDenyReason = Object.assign(
        {
          [BaseModelKeys.createdDate]: new Date(),
          [BaseModelKeys.createdBy]: userEmail,
          [ApproveDenyReasonKeys.activity]: formData?.reason ?? null,
          [ApproveDenyReasonKeys.visibilityLevel]: this.changeAgentStatusConfig.approveDenyReasonVisibilityLevel,
        },
        new ApproveDenyReason(),
      );
      const agentId = this.agentDispositionFormDataSig()[BaseModelKeys.dbId];
      const agentStatus = this.changeAgentStatusConfig.agentStatus;

      this.isUpdateStatusInProgress = true;

      Promise.all([
        this.agentService.updateAgentFields(agentId, { [AgentKeys.agent_status]: agentStatus }, this._sourceType),
        formData?.reason && this.agentApproveDenyReasonsService.create(agentId, approveDenyReason, true),
      ])
        .then(() => {
          this.toster.success('Agent Status Successfully set to ' + agentStatus);
          this.updateAgentStatusFormComponent.instance.resetValues();

          e.component.instance.hide();

          this.agentDispositionModalComponent.hideModal();
          this.onAgentUpdated.emit();
        })
        .finally(() => {
          this.isUpdateStatusInProgress = false;
        });
    }
  };

  protected revertChange = () => {
    this.hierarchyManagementComponent?.revertChange();
  };

  protected handleChangeAgentStatusClick = (changeAgentStatusConfig: ChangeAgentStatusConfig) => {
    this.changeAgentStatusConfig = changeAgentStatusConfig;
    this.updateAgentStatusModalComponent?.showModal();
  };

  protected asyncManagerAssignmentValidation = async (e: ValidationCallbackData) => {
    const agent = this.agentDispositionFormDataSig();
    const agentLeadId = agent?.[AgentKeys.manager_id];
    const agentId = agent?.[AgentKeys.p_agent_id];
    const currentAgentLeadId = e.value;

    if (!agentLeadId) {
      return true;
    }
    // Prevent assigning self as Team Lead (by agentId)
    if (currentAgentLeadId && agentId && currentAgentLeadId === agentId) {
      return false;
    }

    const { issues } = await this.hierarchyValidationService.validateHierarchy(agent as Agent, {
      [AgentKeys.manager_id]: currentAgentLeadId,
    });

    const hasCircularAssignmentIssue = issues?.some(issue => issue?.includes('Circular Team Lead Assignment'));
    if (hasCircularAssignmentIssue) {
      return false;
    }

    return true;
  };

  protected generateAgentId = (): void => {
    this.agentDispositionModalService.generateAgentId(this.sourceType);
  };

  protected forceSave = (payload: Omit<ModalWindowActionPayload, 'type'>) => {
    this.modalWindowService.userAction$.next({
      ...payload,
      type: ModalWindowUserAction.skip,
    });
  };

  protected handleBypassValidation = () => {
    this.agentDispositionModalService.bypassValidationSig.set(true);
    validateDxGroups([this.agentDispositionValidationGroup, this.agentIdValidationGroup]);
  };

  protected uniqAgentIdValidationCallback = async () => {
    const bypassValidation = this.agentDispositionModalService.bypassValidationSig();

    if (bypassValidation) {
      return true;
    }
    const checkedAgentId = this.agentDispositionFormDataSig()[AgentKeys.p_agent_id];

    if (!checkedAgentId) {
      return true;
    }
    const agents = await this.agentSearchService.getAgentsByAgentId(checkedAgentId);

    const uniqAgents = agents?.filter(agent => {
      const dbId = agent?.[BaseModelKeys.dbId];
      return dbId !== this.agentDispositionFormDataSig()?.[BaseModelKeys.dbId];
    });
    const isValid = !uniqAgents?.length;

    return isValid;
  };
}
